import { ChangeEvent, FC, useState, useEffect } from 'react';
import { Text, Flex, Select } from '@chakra-ui/react';
import { useGetRetailerByClientQuery } from 'core/API/authApi';
import { alphaNumericSort } from 'utils/strings';
import { TRetailerProp } from 'shared/types';
import { useTranslation } from 'helpers/languages/translations';

const ARetLangSelector: FC<{
    selectedRetailer: TRetailerProp;
    setSelectedRetailer: (r: TRetailerProp) => void;
    selectedLang: string;
    setSelectedLang: (l: string) => void;
    omitLang?: boolean;
}> = ({ selectedRetailer, setSelectedRetailer, selectedLang, setSelectedLang, omitLang }) => {
    const [availableLangs, setAvailableLangs] = useState<string[]>([]);
    const translate = useTranslation();

    const {
        data: retailersData,
        isLoading: isGetRetailerLoading,
        isError: isGetRetailerError,
    } = useGetRetailerByClientQuery();

    useEffect(() => {
        if (!isGetRetailerError && !isGetRetailerLoading && retailersData) {
            const langSet = new Set<string>();
            retailersData.data.forEach((r) => {
                r.languages.forEach((l) => langSet.add(l));
            });

            setAvailableLangs(Array.from(langSet));
        }
    }, [isGetRetailerError, isGetRetailerError, retailersData]);

    const handleChangeRetailer = (event: ChangeEvent<HTMLSelectElement>) => {
        /* eslint-disable @typescript-eslint/no-non-null-assertion */
        const id = event.target.value;
        const newRetailer = id === 'default' ? 'default' : retailersData!.data.find((r) => r.id === Number(id))!;
        setSelectedRetailer(newRetailer);
    };

    const renderRetailerSelection = () => {
        const sortedRetailers = retailersData && [...retailersData.data];
        sortedRetailers?.sort((a, b) => alphaNumericSort(a.name, b.name));
        const rList =
            !isGetRetailerLoading &&
            !isGetRetailerError &&
            sortedRetailers &&
            sortedRetailers.map((r) => (
                <option key={r.id} value={r.id}>
                    {r.name}
                </option>
            ));

        return (
            <Select
                bg="white"
                value={selectedRetailer === 'default' ? 'default' : selectedRetailer.id}
                onChange={handleChangeRetailer}
                borderColor="gray.200"
                borderRadius={6}
                size="sm"
            >
                <option value="default">{translate('default.retailer')}</option>
                {rList}
            </Select>
        );
    };

    const handleChangeLanguage = (event: ChangeEvent<HTMLSelectElement>) => {
        setSelectedLang(event.target.value);
    };

    const renderLangSelection = () => {
        const langL = availableLangs.map((l) => (
            <option key={l} value={l}>
                {l}
            </option>
        ));

        return langL.length === 1 ? (
            <>
                <Text textTransform="capitalize" ml={5} whiteSpace="nowrap">
                    {langL[0].key}
                </Text>
                <Text ml={2} whiteSpace="nowrap">
                    ({translate('default.lang')})
                </Text>
            </>
        ) : (
            <Select
                onChange={handleChangeLanguage}
                size="sm"
                borderColor="gray.200"
                bg="white"
                borderRadius={6}
                ml="16px"
                value={selectedLang}
            >
                <option value="default">{translate('default.lang')}</option>
                {langL}
            </Select>
        );
    };
    return (
        <Flex>
            {renderRetailerSelection()}
            {!omitLang && renderLangSelection()}
        </Flex>
    );
};

export default ARetLangSelector;
